import { z } from 'zod';
import { IsoDateTime, UuidString } from '../../primitives.js';

/**
 * Attachment — вложение к задаче или к комментарию (exclusive: task_id или
 * comment_id, но не оба). Хранится в MinIO (S3-compatible blob storage),
 * presigned URL flow в BE-S13-7.
 *
 * `virus_scan_status` — FSM: pending → clean | infected | skipped (dev).
 * Background job (BE-S13-8) запускает ClamAV в prod, в dev = skipped.
 *
 * См. `docs/STAITER_UX.md` §11.15 и `be-backlog.md` BE-S12.1-5.
 */

export const VirusScanStatus = z.enum(['pending', 'clean', 'infected', 'skipped']);
export type VirusScanStatus = z.infer<typeof VirusScanStatus>;

export const Attachment = z
  .object({
    id: UuidString,
    entity_id: UuidString,
    /** Один из двух обязателен (задача ИЛИ комментарий, не оба сразу). */
    task_id: UuidString.nullable(),
    comment_id: UuidString.nullable(),
    filename: z.string().min(1).max(255),
    mime_type: z.string().min(1).max(127),
    size_bytes: z.number().int().nonnegative(),
    minio_bucket: z.string().min(1),
    minio_key: z.string().min(1),
    virus_scan_status: VirusScanStatus,
    uploaded_by_user_id: UuidString,
    uploaded_at: IsoDateTime,
  })

  .refine((d) => (d.task_id !== null) !== (d.comment_id !== null), {
    message: 'Attachment должен быть привязан к задаче ИЛИ к комментарию (не к обоим, не к ничему)',
    path: ['task_id'],
  });
export type Attachment = z.infer<typeof Attachment>;

/**
 * B2 (task-card-nav-and-paste §2.4) — денорм вложений в списке комментариев:
 * ровно то, что рисует чип (имя, тип, размер), без minio_key и служебных полей.
 * Читатель — F6 (чипы под комментарием). Схема нарочно НЕ строгая: это
 * response, а новое поле с бэкенда не должно ломать разбор на фронте (B6).
 */
export const AttachmentBrief = z.object({
  id: UuidString,
  filename: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(127),
  size_bytes: z.number().int().nonnegative(),
});
export type AttachmentBrief = z.infer<typeof AttachmentBrief>;
